import React, { useId } from 'react';
import { motion, useReducedMotion, type Transition, type Variants } from 'framer-motion';
import { cn } from '@/lib/utils';

/**
 * The Mindfull mark. Three directions share one API so the brand can switch
 * with a single constant (MINDFULL_MARK below) while the shortlist is open.
 * Rationale, sizes and motion notes live in docs/brand/mindfull-identity.md;
 * compare them side by side at /dev/logos (dev only).
 *
 * - peek: a pocket with two notes sticking out. The notes fan open the way
 *   folder previews do on the canvas, so the logo moves like the product.
 * - brim: a circle filled to the brim. The name, drawn.
 * - pack: a mind full of thoughts, packed tight.
 *
 * Geometry is drawn in a 32-unit box in ink (`currentColor`) only, so the mark
 * inverts with the theme and never needs a knockout color.
 */
export type MindfullMarkVariant = 'peek' | 'brim' | 'pack';

/**
 * still: rest pose, no motion.
 * intro: plays once on mount (notes rise, the level fills, thoughts drop in).
 * rest: animated rest pose; switch back to it after open or loop.
 * open: the hover pose (notes fan, the level brims, thoughts make room).
 * loop: a calm idle loop, for loading and saving states.
 */
export type MindfullMarkMotion = 'still' | 'intro' | 'rest' | 'open' | 'loop';

/** The shipped direction. Swap here (and public/favicon.svg) to rebrand. */
export const MINDFULL_MARK: MindfullMarkVariant = 'peek';

export interface MindfullMarkProps {
  variant?: MindfullMarkVariant;
  /** Rendered size in px. */
  size?: number;
  motion?: MindfullMarkMotion;
  className?: string;
  /** Accessible name. Omit when the mark sits next to the wordmark. */
  title?: string;
}

// Brand motion: springs at 500 / 38, ease-out and ease-pop curves
const SPRING: Transition = { type: 'spring', stiffness: 500, damping: 38 };
const SOFT_SPRING: Transition = { type: 'spring', stiffness: 260, damping: 18 };
const EASE_OUT = [0.22, 1, 0.36, 1] as const;

interface PartProps {
  still: boolean;
}

/* ── Peek ──────────────────────────────────────────────────────────────── */

const peekLeft: Variants = {
  hidden: { y: 15, rotate: 0 },
  in: { y: 0, x: 0, rotate: -12, transition: { ...SOFT_SPRING, delay: 0.12 } },
  rest: { y: 0, x: 0, rotate: -12, transition: SPRING },
  open: { y: -3.5, x: -1.2, rotate: -22, transition: SPRING },
  loop: {
    y: [0, -2.5, 0],
    rotate: [-12, -16, -12],
    transition: { duration: 1.6, ease: 'easeInOut', repeat: Infinity },
  },
};

const peekRight: Variants = {
  hidden: { y: 15, rotate: 0 },
  in: { y: 0, x: 0, rotate: 9, transition: { ...SOFT_SPRING, delay: 0.24 } },
  rest: { y: 0, x: 0, rotate: 9, transition: SPRING },
  open: { y: -5, x: 1.2, rotate: 19, transition: { ...SPRING, delay: 0.04 } },
  loop: {
    y: [0, -2.5, 0],
    rotate: [9, 13, 9],
    transition: { duration: 1.6, ease: 'easeInOut', repeat: Infinity, delay: 0.8 },
  },
};

const peekPocket: Variants = {
  hidden: { scaleY: 1 },
  // The pocket gives a little as the notes land in it
  in: { scaleY: [1, 0.94, 1], transition: { duration: 0.42, delay: 0.3, ease: EASE_OUT } },
  rest: { scaleY: 1 },
  open: { scaleY: 1 },
  loop: { scaleY: 1 },
};

const POCKET = 'M3.5 15 h25 v8.5 a4.5 4.5 0 0 1 -4.5 4.5 h-16 a4.5 4.5 0 0 1 -4.5 -4.5Z';

function Peek({ still, detail }: PartProps & { detail: boolean }) {
  const clip = `mm-peek-${useId().replace(/:/g, '')}`;
  const paper = { originX: 0.5, originY: 1 } as const;
  const leftNote = (
    <>
      <rect x="7.4" y="4.8" width="10" height="16" rx="1.8" stroke="currentColor" strokeWidth="2.2" />
      {detail && <path d="M10.4 9 h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />}
    </>
  );
  const rightNote = (
    <>
      <rect x="15" y="3.4" width="10" height="16" rx="1.8" stroke="currentColor" strokeWidth="2.2" />
      {detail && <path d="M18 7.6 h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />}
    </>
  );
  return (
    <>
      <defs>
        {/* Notes are hidden below the pocket's floor while they rise out of it */}
        <clipPath id={clip}>
          <rect x="-8" y="-12" width="48" height="36" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clip})`}>
        {still ? (
          // Plain groups: motion elements set transform-box: fill-box, which
          // would move the rotate(angle cx cy) pivot
          <>
            <g transform="rotate(-12 12.4 20.8)">{leftNote}</g>
            <g transform="rotate(9 20 19.4)">{rightNote}</g>
          </>
        ) : (
          <>
            <motion.g variants={peekLeft} style={paper}>
              {leftNote}
            </motion.g>
            <motion.g variants={peekRight} style={paper}>
              {rightNote}
            </motion.g>
          </>
        )}
      </g>
      <motion.path
        variants={still ? undefined : peekPocket}
        style={{ originX: 0.5, originY: 1 }}
        d={POCKET}
        fill="currentColor"
      />
    </>
  );
}

/* ── Brim ──────────────────────────────────────────────────────────────── */

// A wave four periods wide (16 units each) so it can drift one period and loop
const WAVE = 'M-8 11.6 q4 -2 8 0 t8 0 t8 0 t8 0 t8 0 t8 0 t8 0 t8 0 V40 H-8Z';

const brimLevel: Variants = {
  hidden: { y: 24 },
  in: { y: 0, transition: { duration: 1.2, ease: EASE_OUT } },
  rest: { y: 0, transition: SPRING },
  open: { y: -1.8, transition: SPRING },
  loop: { y: [0, -1.2, 0], transition: { duration: 2.4, ease: 'easeInOut', repeat: Infinity } },
};

const brimWave: Variants = {
  hidden: { x: 0 },
  in: { x: -16, transition: { duration: 1.4, ease: EASE_OUT } },
  rest: { x: -16, transition: { duration: 0.9, ease: EASE_OUT } },
  open: { x: -24, transition: { duration: 0.9, ease: EASE_OUT } },
  loop: { x: [0, -16], transition: { duration: 2.4, ease: 'linear', repeat: Infinity } },
};

function Brim({ still }: PartProps) {
  const clip = `mm-brim-${useId().replace(/:/g, '')}`;
  return (
    <>
      <defs>
        <clipPath id={clip}>
          <circle cx="16" cy="16" r="12" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clip})`}>
        <motion.g variants={still ? undefined : brimLevel}>
          <motion.path variants={still ? undefined : brimWave} d={WAVE} fill="currentColor" />
        </motion.g>
      </g>
      <circle cx="16" cy="16" r="12" stroke="currentColor" strokeWidth="2.2" />
    </>
  );
}

/* ── Pack ──────────────────────────────────────────────────────────────── */

const THOUGHTS = [
  { cx: 12.2, cy: 19.6, r: 5.6, open: { x: -0.8, y: 0.6 } },
  { cx: 21.2, cy: 20.4, r: 3.9, open: { x: 0.9, y: 0.5 } },
  { cx: 18.6, cy: 12.2, r: 3.4, open: { x: 0.8, y: -0.9 } },
  { cx: 11.6, cy: 10.6, r: 2.3, open: { x: -0.7, y: -1 } },
];

function packThought(i: number, open: { x: number; y: number }): Variants {
  return {
    hidden: { y: -16, scale: 0.6, opacity: 0 },
    in: {
      x: 0,
      y: 0,
      scale: 1,
      opacity: 1,
      transition: { ...SOFT_SPRING, delay: 0.08 + i * 0.11, opacity: { duration: 0.15, delay: 0.08 + i * 0.11 } },
    },
    rest: { x: 0, y: 0, scale: 1, opacity: 1, transition: SPRING },
    open: { ...open, scale: 0.92, transition: { ...SPRING, delay: i * 0.03 } },
    loop: {
      scale: [1, 0.86, 1],
      transition: { duration: 1.6, ease: 'easeInOut', repeat: Infinity, delay: i * 0.2 },
    },
  };
}

function Pack({ still }: PartProps) {
  return (
    <>
      <circle cx="16" cy="16" r="13" stroke="currentColor" strokeWidth="2.2" />
      {THOUGHTS.map((t, i) => (
        <motion.circle
          key={i}
          cx={t.cx}
          cy={t.cy}
          r={t.r}
          fill="currentColor"
          variants={still ? undefined : packThought(i, t.open)}
        />
      ))}
    </>
  );
}

/* ── Mark ──────────────────────────────────────────────────────────────── */

export function MindfullMark({
  variant = MINDFULL_MARK,
  size = 32,
  motion: motionState = 'still',
  className,
  title,
}: MindfullMarkProps) {
  const reduced = useReducedMotion();
  const still = reduced === true || motionState === 'still';

  return (
    <motion.svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      data-variant={variant}
      initial={still ? false : motionState === 'intro' ? 'hidden' : 'rest'}
      animate={still ? undefined : motionState === 'intro' ? 'in' : motionState}
      className={cn('shrink-0 overflow-visible', className)}
    >
      {title ? <title>{title}</title> : null}
      {variant === 'peek' && <Peek still={still} detail={size >= 20} />}
      {variant === 'brim' && <Brim still={still} />}
      {variant === 'pack' && <Pack still={still} />}
    </motion.svg>
  );
}

export default MindfullMark;
